import React, { useEffect, useRef } from "react";
import downloadIcon from "../../assets/images/download.png";
import AudioPlayer from "react-h5-audio-player";
import "react-h5-audio-player/lib/styles.css";
import HorizontalSeparator from "./HorizontalSeparator";
import { useTranslation } from "react-i18next";
import Button from "../ui/Button";
import Icon from "./Icon";
import { AudioPlayerRewindProps } from "../../types/screensTypes";
import styles from "./AudioPlayer.module.css";

const AudioPlayerRewind: React.FC<AudioPlayerRewindProps> = ({
    title,
    src,
    onClickAddToProject,
    showAddtoProject = false,
    hideDownload = false,
    hideSeparator = false,
    isAutoPlay = false,
    onEnded,
    className = "",
    embedded = false,
}) => {
    // Using any type here due to lack of specific type from 'react-h5-audio-player'
    const playerRef = useRef<any>(null);
    const { t: rawT } = useTranslation();
    const t = rawT as any;

    useEffect(() => {
        if (src && playerRef.current) {
            // Reset audio when the src changes
            playerRef.current.audio.current.currentTime = 0;
            if (isAutoPlay) {
                playerRef.current.audio.current.play();
            } else {
                playerRef.current.audio.current.pause();
            }
        }
    }, [src, isAutoPlay]);

    const handleDownload = async () => {
        try {
            const response = await fetch(src, { cache: "reload" });
            if (!response.ok) {
                throw new Error("Failed to fetch audio file.");
            }
            const blob = await response.blob();
            const url = URL.createObjectURL(blob);

            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", `${title}.mp3`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            // Clean up the object URL to free memory
            URL.revokeObjectURL(url);
        } catch (error) {
            console.error("Error downloading audio file:", error);
        }
    };

    return (
        <div
            className={[
                styles.container,
                "audio-player-container",
                embedded || hideSeparator ? styles.containerEmbedded : "",
                className,
            ]
                .filter(Boolean)
                .join(" ")}
            data-rw-control="audio-player"
        >
            {!hideSeparator && <HorizontalSeparator />}
            {title && <h3 className={`${styles.title} audio-title`}>{title}</h3>}
            <div className={`${styles.player} audio-player`}>
                <AudioPlayer
                    ref={playerRef}
                    src={src}
                    customAdditionalControls={[]}
                    customVolumeControls={[]}
                    showJumpControls={false}
                    layout="horizontal"
                    showDownloadProgress={false}
                    autoPlay={isAutoPlay}
                    onEnded={onEnded}
                    customIcons={{
                        play: <Icon name="ic-play" className="rhap-ds-icon rhap-ds-play" />,
                        pause: <Icon name="ic-pause" className="rhap-ds-icon rhap-ds-pause" />,
                    }}
                />
                <div className={`${styles.downloadRow} download-button-container`}>
                    {showAddtoProject && onClickAddToProject && (
                        <Button onClick={onClickAddToProject} label={t("addToProject")} variant="primary" />
                    )}
                    {!hideDownload && (
                        <span className="project-download-link" onClick={handleDownload}>
                            <img src={downloadIcon} alt="Download" className={`${styles.downloadIcon} download-icon`} />
                        </span>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AudioPlayerRewind;
